import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { MentionConfig } from 'angular-mentions';
import { AccountService } from 'app/core';
import { FieldDef } from 'app/core/models';
import { RelatedToType } from 'app/shared/enums';
import { MentionsService } from 'app/shared/services/mentions.service';

@Component({
  selector: 'app-add-field',
  templateUrl: './add-field.component.html',
  styleUrls: ['./add-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddFieldComponent extends OnDestroyMixin implements OnInit {
  @Input() mentionConfig: MentionConfig;
  @Input() set formValue(value: any) {
    this.customFieldForm.setValue({
      name: '',
      label: '',
      type: '',
      options: [''],
      calcField: '',
      calc_code: { value: '', disabled: true },
      record_type: RelatedToType.Contact,
      ...value,
    });

    if (value.label) {
      this.customFieldForm.controls.name.setValue(this.labelToName(value.label));
    }
  }
  @Input() onlyLabelEditable = false;
  @Output() fieldAdded = new EventEmitter<FieldDef>();

  customFieldForm: FormGroup;
  showValues = false;

  get dropDownValue() {
    return this.customFieldForm.get('options') as FormArray;
  }

  get calcField() {
    return this.customFieldForm.get('calcField').value;
  }

  constructor(
    private readonly accountService: AccountService,
    private readonly mentionsService: MentionsService,
  ) {
    super();
    this.mentionConfig = this.mentionsService.getFieldMentionConfig();

    this.customFieldForm = new FormGroup({
      name: new FormControl('', Validators.required),
      label: new FormControl('', Validators.required),
      type: new FormControl('', Validators.required),
      options: new FormArray([]),
      calcField: new FormControl(''),
      calc_code: new FormControl({ value: '', disabled: true }, []),
      record_type: new FormControl(RelatedToType.Contact),
    });

    this.addDropdownValue();
  }

  ngOnInit(): void {
    this.customFieldForm
      .get('type')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((val) => {
        if (val === 'select' || val === 'multi_select') {
          this.showValues = true;
        } else {
          this.showValues = false;
        }
      });

    this.customFieldForm
      .get('label')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((val) => this.customFieldForm.controls.name.setValue(this.labelToName(val)));

    this.customFieldForm
      .get('calcField')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((val) => {
        const field = this.customFieldForm.get('calc_code');
        if (val) {
          field.enable();
          field.setValidators(Validators.required);
          field.updateValueAndValidity();
        } else {
          field.disable();
          field.clearValidators();
        }
      });
  }

  addDropdownValue(): void {
    this.dropDownValue.push(new FormControl(''));
  }

  removeDropdownValue(index: number): void {
    this.dropDownValue.removeAt(index);
  }

  createCustomField(): void {
    const fieldDef = this.customFieldForm.value;

    if (!['select', 'multi_select'].includes(fieldDef.type)) {
      delete fieldDef.options;
    }

    if (fieldDef.type !== 'record') {
      delete fieldDef.record_type;
    }

    if (fieldDef.type === 'multi_select') {
      fieldDef.multi_select = true;
      fieldDef.type = 'select';
    }

    fieldDef.readonly = false;
    this.customFieldForm.disable();

    this.accountService
      .createCustomField(fieldDef)
      .pipe(untilComponentDestroyed(this))
      .subscribe((fieldDef) => this.fieldAdded.emit(fieldDef));
  }

  private labelToName(label: string): string {
    return label?.toLowerCase().split(' ').join('_');
  }
}
